import {
    ActionReducerMap,
    createFeatureSelector,
    createSelector,
    DefaultProjectorFn,
    MemoizedSelector,
    MetaReducer,
} from '@ngrx/store';
import * as fromMspAsn from './asn/reducer';
import * as fromInputGeo from './event-rules/input-geo/reducer';
import * as fromEventRules from './event-rules/reducer';
import { Geo } from './geo/model';
import * as fromMspGeo from './geo/reducer';
import * as fromMspDattoConfig from './integrations/datto/config/reducer';
import * as fromMspHaloConfig from './integrations/halo/config/reducer';
import * as fromMspCwmConfig from './integrations/cwm/config/reducer';
import * as fromMspDattoCreds from './integrations/datto/credentials/reducer';
import * as fromMspHaloCreds from './integrations/halo/credentials/reducer';
import * as fromMspCwmCreds from './integrations/cwm/credentials/reducer';
import * as fromMspDattoMappings from './integrations/datto/mappings/reducer';
import * as fromMspHaloMappings from './integrations/halo/mappings/reducer';
import * as fromMspCwmMappings from './integrations/cwm/mappings/reducer';

import * as fromMspIp from './ip/reducer';
import {
    mspAccessGroupsFeatureKey,
    mspAccessGroupsReducer,
    MspAccessGroupsState,
} from './msp-access-groups/msp-access-groups.reducer';
import * as fromMspGraphUser from './msp-graph-user/reducer';
import * as fromMspUser from './msp-user/reducer';
import * as fromMsp from './msp/reducer';
import { EventRule } from './event-rules/model';
import { selectStatus } from 'projects/angular-clarity/src/app/stores/status.interface';

export const featureKey = 'msp';

export interface State {
    [mspAccessGroupsFeatureKey]: MspAccessGroupsState;
    [fromMsp.featureKey]: fromMsp.State;
    [fromMspUser.featureKey]: fromMspUser.State;
    [fromMspGraphUser.featureKey]: fromMspGraphUser.State;
    // PSA configs
    [fromMspHaloConfig.featureKey]: fromMspHaloConfig.State;
    [fromMspDattoConfig.featureKey]: fromMspDattoConfig.State;
    [fromMspCwmConfig.featureKey]: fromMspCwmConfig.State;
    // PSA Credentials
    [fromMspDattoCreds.featureKey]: fromMspDattoCreds.State;
    [fromMspHaloCreds.featureKey]: fromMspHaloCreds.State;
    [fromMspCwmCreds.featureKey]: fromMspCwmCreds.State;
    // PSA Mappings
    [fromMspDattoMappings.featureKey]: fromMspDattoMappings.State;
    [fromMspHaloMappings.featureKey]: fromMspHaloMappings.State;
    [fromMspCwmMappings.featureKey]: fromMspCwmMappings.State;
    // Event Rules
    [fromEventRules.featureKey]: fromEventRules.State;
    [fromMspAsn.featureKey]: fromMspAsn.State;
    [fromMspIp.featureKey]: fromMspIp.State;
    [fromMspGeo.featureKey]: fromMspGeo.State;
    [fromInputGeo.featureKey]: fromInputGeo.State;
}

export const reducers: ActionReducerMap<State> = {
    [mspAccessGroupsFeatureKey]: mspAccessGroupsReducer,
    [fromMsp.featureKey]: fromMsp.reducer,
    [fromMspUser.featureKey]: fromMspUser.reducer,
    [fromMspGraphUser.featureKey]: fromMspGraphUser.reducer,
    // PSA configs
    [fromMspHaloConfig.featureKey]: fromMspHaloConfig.reducer,
    [fromMspDattoConfig.featureKey]: fromMspDattoConfig.reducer,
    [fromMspCwmConfig.featureKey]: fromMspCwmConfig.reducer,
    // PSA Credentials
    [fromMspDattoCreds.featureKey]: fromMspDattoCreds.reducer,
    [fromMspHaloCreds.featureKey]: fromMspHaloCreds.reducer,
    [fromMspCwmCreds.featureKey]: fromMspCwmCreds.reducer,
    // PSA Mappings
    [fromMspDattoMappings.featureKey]: fromMspDattoMappings.reducer,
    [fromMspHaloMappings.featureKey]: fromMspHaloMappings.reducer,
    [fromMspCwmMappings.featureKey]: fromMspCwmMappings.reducer,
    // Event Rules
    [fromEventRules.featureKey]: fromEventRules.reducer,
    [fromMspAsn.featureKey]: fromMspAsn.reducer,
    [fromMspIp.featureKey]: fromMspIp.reducer,
    [fromMspGeo.featureKey]: fromMspGeo.reducer,
    [fromInputGeo.featureKey]: fromInputGeo.reducer,
};

export const metaReducers: MetaReducer<State>[] = [];

export const selectFeature = createFeatureSelector<State>(featureKey);

const selectMsp = createSelector(selectFeature, (state) => state[fromMsp.featureKey]);
export const selectMspData = createSelector(selectMsp, fromMsp.selectData);
export const selectMspRootUser = createSelector(selectMspData, (res) => res?.root_user);

export const selectMspLoaded = createSelector(selectMsp, fromMsp.selectLoaded); // TODO: refactor to selectMspStatus
export const selectMspLoading = createSelector(selectMsp, fromMsp.selectLoading); // TODO: refactor to selectMspStatus
export const selectMspUpdating = createSelector(selectMsp, fromMsp.selectUpdating); // TODO: refactor to selectMspStatus
export const selectMspError = createSelector(selectMsp, fromMsp.selectError); // TODO: refactor to selectMspStatus
export const selectMspStatus = createSelector(selectMsp, selectStatus);

export const selectShowStonlyTrial = createSelector(selectMsp, (state) => state.showStonlyTrial);

const selectMspUser = createSelector(selectFeature, (state) => state[fromMspUser.featureKey]);
export const selectMspUsers = createSelector(selectMspUser, fromMspUser.selectAll);
export const selectMspUsersStatus = createSelector(selectMspUser, selectStatus);

const selectMspGraphUser = createSelector(selectFeature, (state) => state[fromMspGraphUser.featureKey]);
export const selectMspGraphMspUsers = createSelector(selectMspGraphUser, fromMspGraphUser.selectAll);
export const selectMspGraphUsersStatus = createSelector(selectMspUser, selectStatus);

// PSA Configs
const selectMspHaloConfig = createSelector(selectFeature, (state) => state[fromMspHaloConfig.featureKey]);
export const selectMspHaloConfigStatus = createSelector(selectMspHaloConfig, selectStatus);
export const selectMspHaloConfigData = createSelector(selectMspHaloConfig, fromMspHaloConfig.selectData);

const selectMspDattoConfig = createSelector(selectFeature, (state) => state[fromMspDattoConfig.featureKey]);
export const selectMspDattoConfigStatus = createSelector(selectMspDattoConfig, selectStatus);
export const selectMspDattoConfigData = createSelector(selectMspDattoConfig, fromMspDattoConfig.selectData);

const selectMspCwmConfig = createSelector(selectFeature, (state) => state[fromMspCwmConfig.featureKey]);
export const selectMspCwmConfigStatus = createSelector(selectMspCwmConfig, selectStatus);
export const selectMspCwmConfigData = createSelector(selectMspCwmConfig, fromMspCwmConfig.selectData);

// PSA Credentials
const selectMspDattoCreds = createSelector(selectFeature, (state) => state[fromMspDattoCreds.featureKey]);
export const selectMspDattoCredsStatus = createSelector(selectMspDattoCreds, selectStatus);
export const selectMspDattoCredsData = createSelector(selectMspDattoCreds, fromMspDattoCreds.selectDataAndConnection);

const selectMspHaloCreds = createSelector(selectFeature, (state) => state[fromMspHaloCreds.featureKey]);
export const selectMspHaloCredsStatus = createSelector(selectMspHaloCreds, selectStatus);
export const selectMspHaloCredsData = createSelector(selectMspHaloCreds, fromMspHaloCreds.selectDataAndConnection);

const selectMspCwmCreds = createSelector(selectFeature, (state) => state[fromMspCwmCreds.featureKey]);
export const selectMspCwmCredsStatus = createSelector(selectMspCwmCreds, selectStatus);
export const selectMspCwmCredsData = createSelector(selectMspCwmCreds, fromMspCwmCreds.selectDataAndConnection);

// PSA Mappings
const selectMspDattoMappings = createSelector(selectFeature, (state) => state[fromMspDattoMappings.featureKey]);
export const selectMspDattoMappingsStatus = createSelector(selectMspDattoMappings, selectStatus);
export const selectMspDattoMappingsData = createSelector(selectMspDattoMappings, fromMspDattoMappings.selectAll);

const selectMspHaloMappings = createSelector(selectFeature, (state) => state[fromMspHaloMappings.featureKey]);
export const selectMspHaloMappingsStatus = createSelector(selectMspHaloMappings, selectStatus);
export const selectMspHaloMappingsData = createSelector(selectMspHaloMappings, fromMspHaloMappings.selectAll);

const selectMspCwmMappings = createSelector(selectFeature, (state) => state[fromMspCwmMappings.featureKey]);
export const selectMspCwmMappingsStatus = createSelector(selectMspCwmMappings, selectStatus);
export const selectMspCwmMappingsData = createSelector(selectMspCwmMappings, fromMspCwmMappings.selectAll);

// Event Rules
const selectEventRules = createSelector(selectFeature, (state) => state[fromEventRules.featureKey]);
export const selectEventRulesAll = createSelector(selectEventRules, fromEventRules.selectAll);
export const selectEventRulesEntities = createSelector(selectEventRules, fromEventRules.selectEntities);
export const selectEventRulesStatus = createSelector(selectEventRules, selectStatus);
export const selectEventRuleById = (id: string): MemoizedSelector<object, EventRule, DefaultProjectorFn<EventRule>> =>
    createSelector(selectEventRules, fromEventRules.selectEntity(id));

export const selectEventRulesForTenantTargets = (tenant_id: string) =>
    createSelector(selectEventRulesAll, (rules) =>
        rules.filter((rule) => rule.target_tenants.some((t) => t === tenant_id)),
    );

export const selectEventRulesForUserTargets = (tenant_id: string) =>
    createSelector(selectEventRulesAll, (rules) =>
        rules.filter((rule) => rule.target_users.some((user) => user.tenant_id === tenant_id)),
    );

const selectMspAsn = createSelector(selectFeature, (state) => state[fromMspAsn.featureKey]);
export const selectMspASNs = createSelector(selectMspAsn, fromMspAsn.selectAll);
export const selectMspASNsStatus = createSelector(selectMspAsn, selectStatus);
export const selectMspASNsTotal = createSelector(selectMspAsn, fromMspAsn.selectTotal);

const selectMspIp = createSelector(selectFeature, (state) => state[fromMspIp.featureKey]);
export const selectMspIps = createSelector(selectMspIp, fromMspIp.selectAll);
export const selectMspIpsStatus = createSelector(selectMspIp, selectStatus);
export const selectMspIpsTotal = createSelector(selectMspIp, fromMspIp.selectTotal);

const selectMspGeo = createSelector(selectFeature, (state) => state[fromMspGeo.featureKey]);
export const selectMspGeos = createSelector(selectMspGeo, fromMspGeo.selectAll);
export const selectMspGeosStatus = createSelector(selectMspGeo, selectStatus);
export const selectMspGeosTotal = createSelector(selectMspGeo, fromMspGeo.selectTotal);

const selectInputGeo = createSelector(selectFeature, (state) => state[fromInputGeo.featureKey]);
export const selectInputGeoAll = createSelector(selectInputGeo, fromInputGeo.selectAll);

export const selectGeosForTenant = (tenant_id: string) =>
    createSelector(
        selectEventRulesForTenantTargets(tenant_id),
        selectMspGeos,
        selectInputGeoAll,
        (rules, geos, inputs) => {
            // all geos used by tenant_id (once each)
            const results = new Map<string, Geo>();
            for (const rule of rules) {
                for (const input of inputs) {
                    if (input.event_rule_id === rule.id) {
                        results.set(input.geo_id, geos[input.geo_id]);
                    }
                }
            }
            return [...results.values()];
        },
    );
